// imports section
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Breadcrumbs,
  Link,
  AppBar,
  Checkbox,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
  capitalize,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Navbar from "../../components/common/Navbar";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme";
import {
  CON_STATUS_ACTIVE,
  CON_STATUS_DELETED,
} from "../../utils/sys/Constants";
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import { EMPLOYEE_API_01, EMPLOYEE_API_02 } from "../../api/ApiModel";
import SnackbarMessage from "../../components/common/SnackbarMessage";
import {
  MSG_GROUPACCESS_SUCCESS,
  MSG_GROUPACCESS_FAILED,
} from "../../utils/sys/Messages";

const RichTreeViewWithCheckboxes = ({
  items,
  checkedItems,
  onCheckboxChange,
}) => {
  const [expanded, setExpanded] = useState({});
  const theme = useTheme();
  const styles = useStyles(theme);

  const handleToggle = (id) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [id]: !prevExpanded[id],
    }));
  };

  const handleCheckboxChange = (id) => {
    onCheckboxChange(id);
  };

  const isChecked = (id) => checkedItems.includes(id);

  return (
    <Box
      sx={{
        paddingLeft: 2,
        // border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "16px",
        // backgroundColor: "#f9f9f9",
        minWidth: "100%",
      }}
    >
      {items.map((item) => (
        <Box
          key={item.id}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          sx={{ marginBottom: 1, paddingLeft: item.level * 2 }}
        >
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="flex-start"
            sx={{
              padding: "4px 8px",
              width: "100%",
              borderRadius: "4px",
              // textDecoration: "underline",
              boxShadow: expanded[item.id]
                ? `0px 0px 2px 0px ${theme.palette.text.icons}`
                : "transparent",

              backgroundColor: expanded[item.id]
                ? theme.palette.background.tertiary
                : "transparent",
              "&:hover": {
                backgroundColor: theme.palette.background.tertiary,
              },
              cursor: "pointer",
            }}
          >
            {item.children && item.children.length > 0 && (
              <IconButton
                size="small"
                onClick={() => handleToggle(item.id)}
                sx={{
                  marginRight: 1,

                  width: "100%",
                  borderRadius: "4px",
                }}
              >
                {expanded[item.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                <Typography
                  variant="body2"
                  fontWeight="600"
                  textAlign="left"
                  sx={{ flexGrow: 1, pl: 1, textTransform: "capitalize" }}
                >
                  {item.label}
                </Typography>
              </IconButton>
            )}
          </Box>
          {item.children && (
            <Collapse in={expanded[item.id]} timeout="auto" unmountOnExit>
              <Box sx={{ paddingLeft: 5, pt: 1 }}>
                {" "}
                {/* Indentation for child items */}
                {item.children.map((child) => (
                  <Box
                    key={child.id}
                    display="flex"
                    alignItems="center"
                    sx={{ marginBottom: 1 }}
                  >
                    <Checkbox
                      checked={isChecked(child.id)}
                      onChange={() => handleCheckboxChange(child.id)}
                      sx={{ padding: "4px", marginRight: 1 }}
                    />
                    <Typography variant="body2">{child.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          )}
        </Box>
      ))}
    </Box>
  );
};

const GroupAccess = () => {
  // Hooks
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyles(theme);

  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [accessRights, setAccessRights] = useState([]);
  const [checkedItems, setCheckedItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, BUSINESSTYPECODE, USERNAME, GROUPCODE } =
    SYS_USER.USER_PARAM || {};

  useEffect(() => {
    fetchUserGroups();
    updateUserAccess();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchUserAccess(selectedGroup).catch((error) => {
        setError("Failed to fetch user access rights.");
        console.error(error);
      });
    }
  }, [selectedGroup]);

  // Handlers

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleGroupChange = (event) => {
    setSelectedGroup(event.target.value);
  };

  const handleCheckboxChange = (id) => {
    setCheckedItems((prevCheckedItems) => {
      const isChecked = prevCheckedItems.includes(id);
      let newCheckedItems = [...prevCheckedItems];

      if (isChecked) {
        newCheckedItems = newCheckedItems.filter((item) => item !== id);
      } else {
        newCheckedItems.push(id);
      }

      return newCheckedItems;
    });
  };

  const updateUserAccess = async () => {
    setLoading(true);
    try {
      const useraccessdt = checkedItems
        .map((itemId) => {
          const accessItem = accessRights.allAccessRights.find(
            (accessItem) => accessItem.MODMENCODE === itemId
          );
          if (!accessItem) return null;

          return {
            groupcode: String(selectedGroup),
            modulecode: String(accessItem.MODULECODE),
            modmencode: String(itemId),
            sts: CON_STATUS_ACTIVE,
          };
        })
        .filter((item) => item); // Remove null items

      if (useraccessdt.length === 0) {
        console.warn("No submenu items selected for update.");
        return;
      }

      const payload = {
        useraccessdt,
        groupcode: String(selectedGroup),
        locdate: String(new Date().toISOString().split("T")[0]),
        enuser: USERNAME,
        stsd: CON_STATUS_DELETED,
      };

      const response = await externalpostApi(EMPLOYEE_API_02, payload);

      if (response) {
        await fetchUserAccess(selectedGroup);
        setSnackbarMessage(MSG_GROUPACCESS_SUCCESS);
        setSnackbarSeverity("success");
      } else {
        console.error("Failed to update user access. Response was empty.");
        setSnackbarMessage(MSG_GROUPACCESS_FAILED);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("Error updating user access:", error);
      setSnackbarMessage(MSG_GROUPACCESS_FAILED);
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  const transformAccessRights = () => {
    if (!accessRights || !accessRights.allAccessRights) return [];

    const { allAccessRights } = accessRights;

    const groupedAccessRights = allAccessRights.reduce((acc, item) => {
      acc[item.MODULECODE] = acc[item.MODULECODE] || {
        label: item.MODULENAME,
        children: [],
      };
      acc[item.MODULECODE].children.push({
        id: item.MODMENCODE,
        label: item.MODMENUNAME,
        children: [],
      });
      return acc;
    }, {});

    return Object.keys(groupedAccessRights).map((key) => ({
      id: key,
      label: groupedAccessRights[key].label,
      children: groupedAccessRights[key].children.map((child) => ({
        id: child.id,
        label: child.label,
        children: [],
      })),
    }));
  };

  // API calls

  // Fetch user groups from API
  const fetchUserGroups = async () => {
    setLoading(true);
    try {
      const params = {
        COMPCODE,
        LOCCODE,
        BUSINESSTYPECODE,
        STS: CON_STATUS_ACTIVE,
      };
      const response = await externalgetApi(EMPLOYEE_API_01, "GET", params);
      setUserGroups(response); // Assuming response is an array of user groups
    } catch (error) {
      setError("Failed to fetch user groups.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch access rights data based on selected group
  const fetchUserAccess = async (groupCode) => {
    setLoading(true);
    try {
      const response = await externalgetApi(EMPLOYEE_API_02, "GET", {
        GROUPCODE: groupCode,
        BUSINESSTYPECODE,
        STS: CON_STATUS_ACTIVE,
      });

      setAccessRights(response);

      // Ensure response.mappedPrivillages exists before mapping
      const newCheckedItems = response.mappedPrivillages
        ? response.mappedPrivillages.map((item) => item.MODMENCODE)
        : [];
      setCheckedItems(newCheckedItems);
    } catch (error) {
      setError("Failed to fetch user access rights.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs}
                position="fixed"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none" href="/">
                  Home
                </Link>
                <Link underline="none" href="/Employee">
                  Employee
                </Link>
                <Link underline="none" href="/">
                  Group Access
                </Link>
              </Breadcrumbs>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.box_container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={styles.col_title}>
              <Typography variant="title" align="left">
                User Access
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
            <Box
              sx={{
                ...styles.col_1,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                textAlign: "left",
                padding: 2,
                marginLeft: 0,
                height: "70vh",
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="group-select-label">User Group</InputLabel>
                <Select
                  labelId="group-select-label"
                  id="group-select"
                  label="User Group"
                  value={selectedGroup}
                  onChange={handleGroupChange}
                >
                  {userGroups.map((group) => (
                    <MenuItem key={group.GROUPCODE} value={group.GROUPCODE}>
                      {group.GROUPNAME}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  marginTop: 2,
                  width: "20%",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={updateUserAccess}
                  sx={styles.btn_primary}
                >
                  Confirm
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
            <Box
              sx={{
                // ...styles.col_1,
                backgroundColor: theme.palette.background.paper,
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                textAlign: "left",
                borderRadius: "4px",
                padding: 0,
                height: "100%",
                maxHeight: "70vh",
                overflow: "auto",
                height: "70vh",
              }}
            >
              {loading ? (
                <CircularProgress />
              ) : error ? (
                <Typography color="error">{error}</Typography>
              ) : (
                <RichTreeViewWithCheckboxes
                  items={transformAccessRights()}
                  checkedItems={checkedItems}
                  onCheckboxChange={handleCheckboxChange}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <SnackbarMessage
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
};

export default GroupAccess;
